import { updatePageProps } from '@eventbrite/datalayer-library';
import {
    hasUrgencySignalsEnabled,
    UrgencySignalsProps,
} from '@eventbrite/urgency-signals';
import { EventDescriptionProps } from '../components/EventDetails/EventDescription/EventDescription';
import { FaqItem } from '../components/EventDetails/Widgets/Faq';
import {
    CheckoutWidgetProps,
    Event,
    EventDetailsProps,
    EventHeroScopedProps,
    User,
} from '../types';

const hasWidgetType = (
    widgetType: String,
    eventDescription?: EventDescriptionProps,
) => {
    return eventDescription?.structuredContent?.widgets.some(
        (widget) => widget.type === widgetType,
    );
};

const getFaqsData = (eventDescription?: EventDescriptionProps): FaqItem[] => {
    const faqWidget = eventDescription?.structuredContent?.widgets.find(
        (widget) => widget.type === 'faqs',
    )?.data || { faqs: [] };
    return 'faqs' in faqWidget ? faqWidget?.faqs : [];
};

const hasVideoModule = (eventDescription?: EventDescriptionProps): Boolean => {
    return !!eventDescription?.structuredContent?.modules.find(
        (module) => module.type === 'video',
    );
};

const normalizeCategory = (category: string) => {
    return category.toLocaleLowerCase().replace(/\s/g, '_');
};

export const dataLayerMiddleware = (
    event?: Event,
    components?: {
        eventDescription?: EventDescriptionProps;
        eventDetails?: EventDetailsProps;
        eventHero?: EventHeroScopedProps;
        checkoutWidget?: CheckoutWidgetProps;
    },
    urgencySignals?: UrgencySignalsProps,
    user?: User,
) => {
    const eventDescription = components?.eventDescription;
    const eventDetails = components?.eventDetails;
    const eventHero = components?.eventHero;
    const hasHeroVideo = !!components?.eventHero?.featuredVideo?.url;
    const page = {
        category: event?.category ? normalizeCategory(event.category) : '',
        eventId: event?.id,
        eventEndDate: event?.end.utc,
        eventStartDate: event?.start.utc,
        eventStatus: event?.salesStatus.sales_status,
        faqsNumber: getFaqsData(eventDescription)?.length,
        format: event?.format ? normalizeCategory(event.format) : '',
        hasAgenda: hasWidgetType('agenda', eventDescription),
        hasAutogeneratedContent:
            !!eventDescription?.structuredContent?.hasAutogeneratedContent,
        hasEarlyBirdUrgencySignal: urgencySignals?.categories.earlyBird,
        hasFaqs: hasWidgetType('faqs', eventDescription),
        hasFewTicketsLeftUrgencySignal: urgencySignals?.messages.fewTickets,
        hasGoingFastUrgencySignal: urgencySignals?.messages.goingFast,
        hasImages: eventHero?.items?.length,
        hasNewUrgencySignal: urgencySignals?.categories.new,
        hasOpenDiscount: event?.discount?.discountType === 'open',
        hasPopularUrgencySignal: urgencySignals?.categories.popular,
        hasRSVP: event?.shouldDisplayRsvp,
        hasSalesEndSoonUrgencySignal: urgencySignals?.messages.salesEndSoon,
        hasUrgencySignals: hasUrgencySignalsEnabled(urgencySignals),
        hasVideo: hasVideoModule(eventDescription) || hasHeroVideo,
        heroVideo: hasHeroVideo,
        isAuthenticated: user?.isAuthenticated,
        isFree: !eventDetails?.refund.isPaidEvent,
        isListed: event?.isListed,
        isLive: event?.isLive,
        isOnline: event?.isOnlineEvent,
        isPaid: eventDetails?.refund?.isPaidEvent,
        isPrivate: event?.isProtected,
        isRepeatingEvent: event?.isSeriesParent && !!event?.childEvents,
        isSeriesParent: event?.isSeriesParent,

        subcategory: event?.subcategory
            ? normalizeCategory(event.subcategory)
            : '',
    };

    updatePageProps({ page });
};
